import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import { hybridApiBaseUrl, hybridFlags } from '../services/hybridFlags';

const VERIFONE_CALLBACK_FUNCTION_URL =
  process.env.REACT_APP_VERIFONE_CALLBACK_URL ||
  (hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads
    ? `${String(hybridApiBaseUrl).replace(/\/+$/, '')}/api/public/verifone-payment-callback`
    : 'https://us-central1-my-repair-lab.cloudfunctions.net/verifonePaymentCallback');

/** ערכים שמעידים על הצלחה / ביטול / כישלון – לפי מה ש־Verifone מוסיף ל־URL. 00=אושר, 0 לבד לא נחשב הצלחה (יכול להיות דחייה). */
const SUCCESS_VALUES = /^(success|approved|authorised|authorized|transaction_success|transaction_approved|ok|completed|paid|00)$/i;
const CANCEL_VALUES = /^(cancel|cancelled|cancelled_by_user|aborted)$/i;
const FAIL_VALUES = /^(declined|failed|rejected|error|denied|0085|05|6|false)$/i;
const FAIL_REASON_CODE = /^1\d{3}$/; // 1000-1999
const UNRESOLVED_MAX_ATTEMPTS = 5;
const UNRESOLVED_RETRY_DELAY_MS = 3000;

/**
 * דף ציבורי לחזרה מ-Verifone אחרי תשלום.
 * הסטטוס נקבע לפי הפרמטרים ש-Verifone מוסיף ל-URL (result, outcome וכו') – לא לפי status קבוע.
 * רק כפתור "סגור חלון" – אין כפתור חזרה למערכת או התחברות.
 */
const PaymentCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>('');
  /** סטטוס תצוגה – נקבע לפי תגובת השרת (מקור האמת), לא רק לפי URL */
  const [displayStatus, setDisplayStatus] = useState<'success' | 'cancel' | 'error' | 'unknown'>('unknown');

    const repairId = searchParams.get('repairId') || '';
    const callNumber = searchParams.get('callNumber') || '';
    const paymentKind = searchParams.get('paymentKind') || '';
    const paymentSource = searchParams.get('paymentSource') || '';
  const message = searchParams.get('message') || searchParams.get('reason') || '';
  const resultParam = (searchParams.get('result') || '').trim();
  const outcomeParam = (searchParams.get('outcome') || '').trim();
  const norm = (s: string) => s.toLowerCase().replace(/-/g, '_');
  // אם Verifone שלח result או outcome שמצביעים על הצלחה – תמיד נזהה הצלחה (גם אם status=error)
  const resultOutcomeSuccess = SUCCESS_VALUES.test(norm(resultParam)) || SUCCESS_VALUES.test(norm(outcomeParam));
  const rawStatus = (
    resultParam ||
    outcomeParam ||
    searchParams.get('authResult') ||
    searchParams.get('transaction_result') ||
    searchParams.get('response_code') ||
    searchParams.get('code') ||
    searchParams.get('status') ||
    ''
  ).trim();
  const statusNorm = norm(rawStatus);

  const responseCode = (searchParams.get('response_code') || searchParams.get('code') || '').trim();
  const isCancel = CANCEL_VALUES.test(statusNorm);
  const isFail = FAIL_VALUES.test(statusNorm) || FAIL_REASON_CODE.test(statusNorm) || ['05', '0085', '6'].includes(responseCode);
  const isSuccess = !isFail && (resultOutcomeSuccess || SUCCESS_VALUES.test(statusNorm));
  const effectiveStatus = isSuccess ? 'success' : isCancel ? 'cancel' : isFail ? 'error' : 'unknown';

  useEffect(() => {
    if (!repairId && !callNumber) {
      setLoading(false);
      setErrorMsg('חסר מזהה תיקון: הוסף repairId או callNumber (מספר קריאה) לכתובת.');
      return;
    }

    // איסוף כל הפרמטרים – גם מ-query וגם מ-hash (Verifone לפעמים מוסיף ב-hash)
    const allUrlParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      allUrlParams[key] = value;
    });
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashPart = window.location.hash.slice(1);
      const hashQuery = hashPart.includes('?') ? hashPart.split('?')[1] : hashPart;
      new URLSearchParams(hashQuery).forEach((value, key) => {
        allUrlParams[key] = value;
      });
    }
    const receivedAt = new Date().toISOString();
    console.log('[PaymentCallback] Full URL:', window.location.href);
    console.log('[PaymentCallback] All params (query + hash):', allUrlParams);
    console.log('[PaymentCallback] Received at:', receivedAt);

    // תמיד קוראים לשרת עם כל הפרמטרים – השרת יפרש את הסטטוס מכל הערכים
    const params = new URLSearchParams({ status: effectiveStatus });
    if (repairId) params.set('repairId', repairId);
    else params.set('callNumber', callNumber);
    if (paymentKind) params.set('paymentKind', paymentKind);
    if (paymentSource) params.set('paymentSource', paymentSource);
    if (message) params.set('message', message);
    if (rawStatus) params.set('rawResult', rawStatus);
    if (resultParam) params.set('verifone_result', resultParam);
    if (outcomeParam) params.set('verifone_outcome', outcomeParam);
    params.set('allUrlParams', JSON.stringify(allUrlParams));
    params.set('receivedAt', receivedAt);
    const url = `${VERIFONE_CALLBACK_FUNCTION_URL}?${params.toString()}`;

    // Verifone לפעמים מסמן את העסקה כמאושרת רק כמה שניות אחרי החזרה לדף — אם השרת
    // עדיין לא מצליח לקבוע סטטוס, מנסים שוב לפני שמציגים משהו ללקוח
    const fetchCallback = async (): Promise<any> => {
      let data: any = null;
      for (let attempt = 1; attempt <= UNRESOLVED_MAX_ATTEMPTS; attempt += 1) {
        const res = await fetch(url, { method: 'GET' });
        data = await res.json();
        if (data?.success || !data?.unresolved) return data;
        if (attempt < UNRESOLVED_MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, UNRESOLVED_RETRY_DELAY_MS));
        }
      }
      return data;
    };

    fetchCallback()
      .then((data) => {
        console.log('[PaymentCallback] Backend response:', data, 'at', new Date().toISOString());
        if (data?.success) {
          try {
            const resolvedRepairId = String(data?.repairId || repairId || '').trim();
            if (resolvedRepairId) {
              const payload = {
                repairId: resolvedRepairId,
                paymentStatus: String(data?.paymentStatus || ''),
                supplementCompleted: Boolean(data?.supplementCompleted),
                at: new Date().toISOString(),
              };
              localStorage.setItem('repair-payment-updated', JSON.stringify(payload));
              window.dispatchEvent(new CustomEvent('repair-payment-updated', { detail: payload }));
            }
          } catch {
            // ignore storage failures (private mode / quota)
          }
        }
        if (data.success) {
          setPaymentStatusMessage(data.paymentStatusMessage || '');
          if (data.paymentDate) setPaymentDate(data.paymentDate);
          // מקור האמת: השרת קבע את הסטטוס – כך סירוב לא יוצג כהצלחה
          const backendStatus = data.paymentStatus;
          const isSupplement = String(data?.paymentKind || paymentKind || '').trim().toLowerCase() === 'supplement';
          if (backendStatus === 'completed' || data?.supplementCompleted) {
            setDisplayStatus('success');
          } else if (backendStatus === 'failed') setDisplayStatus('error');
          else setDisplayStatus(effectiveStatus as 'success' | 'cancel' | 'error' | 'unknown');
        } else if (data?.unresolved) {
          // לא ידוע ≠ נכשל: הסנכרון במערכת יעדכן את הקריאה כשהעסקה תאושר ב-Verifone
          if (data.allUrlParams) console.warn('[PaymentCallback] Params received (for debugging):', data.allUrlParams);
          setPaymentStatusMessage('התשלום בבדיקה מול חברת האשראי. אם החיוב בוצע, הוא יתעדכן במערכת אוטומטית.');
          setDisplayStatus('unknown');
        } else {
          if (data.allUrlParams) console.warn('[PaymentCallback] Params received (for debugging):', data.allUrlParams);
          setErrorMsg(data.error || 'שגיאה בעדכון סטטוס התשלום');
          setDisplayStatus('error');
        }
      })
      .catch((err) => {
        console.error('[PaymentCallback] Fetch error:', err);
        setErrorMsg(err?.message || 'שגיאה בתקשורת עם השרת');
        setDisplayStatus('error');
      })
      .finally(() => setLoading(false));
    // ריצה לפי זהות תיקון/קריאה; פרמטרי URL נשארים מחוץ ל-deps כדי לא לרוץ מחדש בכל רינדר
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairId, callNumber]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography>מעדכן סטטוס תשלום...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper elevation={2} sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'right' }}>
            {errorMsg}
          </Alert>
        )}

        {!(repairId || callNumber) ? (
          <>
            <ErrorIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              קישור לא תקין
            </Typography>
            <Typography variant="body2" color="text.secondary">
              הוסף לכתובת repairId או callNumber (מספר קריאה).
            </Typography>
          </>
        ) : displayStatus === 'success' ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {paymentKind === 'supplement' || searchParams.get('paymentKind') === 'supplement'
                ? 'תוספת התשלום הושלמה בהצלחה'
                : 'התשלום הושלם בהצלחה'}
            </Typography>
            {paymentDate && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {new Date(paymentDate).toLocaleDateString('he-IL', { dateStyle: 'medium' })}
              </Typography>
            )}
            {paymentStatusMessage && (
              <Typography variant="body2" color="text.secondary">
                {paymentStatusMessage}
              </Typography>
            )}
            <Button variant="contained" onClick={() => window.close()} sx={{ mt: 3 }}>
              סגור חלון
            </Button>
          </>
        ) : displayStatus === 'cancel' ? (
          <>
            <CancelIcon sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              התשלום בוטל
            </Typography>
            {paymentStatusMessage && (
              <Typography variant="body2" color="text.secondary">
                {paymentStatusMessage}
              </Typography>
            )}
            <Button variant="contained" onClick={() => window.close()} sx={{ mt: 3 }}>סגור חלון</Button>
          </>
        ) : displayStatus === 'unknown' ? (
          <>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              סטטוס התשלום
            </Typography>
            {paymentStatusMessage ? (
              <Typography variant="body2" color="text.secondary">
                {paymentStatusMessage}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                ניתן לבדוק את סטטוס התיקון במערכת.
              </Typography>
            )}
            <Button variant="contained" onClick={() => window.close()} sx={{ mt: 3 }}>סגור חלון</Button>
          </>
        ) : (
          <>
            <ErrorIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              התשלום נכשל
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              סיבת הכישלון:
            </Typography>
            <Alert severity="error" sx={{ textAlign: 'right', mb: 2 }}>
              {(paymentStatusMessage || errorMsg) || 'העסקה נדחתה. יש לוודא שכל השדות מולאו באופן תקין, או לפנות לתמיכה.'}
            </Alert>
            <Button variant="contained" onClick={() => window.close()} sx={{ mt: 2 }}>סגור חלון</Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentCallbackPage;
